/**
 * EvalReportsPage — Container page with tab navigation.
 * Tabs: Continuous Evaluation (default) | Pre-Deployment Evaluation
 *
 * URL strategy:
 *   - No "tab" param or tab=continuous → Continuous (default)
 *   - tab=pre-deployment → Pre-Deployment
 *   - run=xxx&view=report params are preserved when switching tabs
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext, useSearchParams } from "react-router-dom";
import ContinuousEvalTab from "./tabs/ContinuousEvalTab";
import PreDeploymentEvalTab from "./tabs/PreDeploymentEvalTab";
import "./EvalReportsPage.css";
import { DEFAULT_EVAL_AGENT, evalAgentLabel } from "../../agents";

type TabId = "continuous" | "pre-deployment";

interface TabDef {
  id: TabId;
  labelKey: "reports.online" | "reports.offline";
}

const TABS: TabDef[] = [
  { id: "continuous", labelKey: "reports.online" },
  { id: "pre-deployment", labelKey: "reports.offline" },
];

export default function EvalReportsPage() {
  const { t } = useTranslation("eval");
  const agent = useOutletContext<{ agent?: string } | undefined>()?.agent ?? DEFAULT_EVAL_AGENT;
  const [searchParams, setSearchParams] = useSearchParams();

  // Date range state (default: last 7 days)
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

  // Default tab is "continuous" when no tab param present
  const activeTab: TabId =
    searchParams.get("tab") === "pre-deployment" ? "pre-deployment" : "continuous";

  const switchTab = (tabId: TabId) => {
    if (tabId === "continuous") {
      // Default tab — remove tab param, clear run/view state
      setSearchParams({});
    } else {
      // Non-default tab — set tab param, clear run/view state
      setSearchParams({ tab: tabId });
    }
  };

  return (
    <div className="eval-reports">
      <div className="eval-reports__header">
        <div className="eval-reports__header-left">
          <h1 className="eval-reports__title">{activeTab === "continuous"
            ? t("reports.title", { agent: evalAgentLabel(agent) })
            // Pre-deployment runs are platform-wide: not named after the selected agent.
            : t("reports.platformTitle")}</h1>
          <p className="eval-reports__subtitle">{t("shared.lastUpdate", { time: new Date().toLocaleString(t("shared.updatedLocale") || undefined) })}</p>
        </div>
        <div className="eval-reports__date-picker">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <span>—</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {/* Tab bar */}
      <div className="eval-reports__tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`eval-reports__tab${activeTab === tab.id ? " eval-reports__tab--active" : ""}`}
            onClick={() => switchTab(tab.id)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="eval-reports__content">
        {activeTab === "continuous" && <ContinuousEvalTab startDate={startDate} endDate={endDate} />}
        {activeTab === "pre-deployment" && <PreDeploymentEvalTab />}
      </div>
    </div>
  );
}
